import { getAddress, isHex, pad, toHex } from 'viem';

/** Checksummed address for CLOB maker / signer / L1 `address` (Polymarket compares these strictly). */
export function normalizeClobAddress(address: string): string {
  try {
    return getAddress(address as `0x${string}`);
  } catch {
    return address;
  }
}

/**
 * Normalizes a builder code to a 32-byte hex string (bytes32).
 * Handles numeric strings, short hex strings, and missing values.
 *
 * @param code - The builder code from environment or config
 * @returns A padded 66-character hex string starting with 0x
 */
export function formatBuilderCode(code?: string | number): string {
  if (!code) return '0x0000000000000000000000000000000000000000000000000000000000000000';

  const strCode = String(code).trim();

  // If it's already a full 32-byte hex string, return as-is
  if (isHex(strCode) && strCode.length === 66) return strCode;

  // If it's a hex string but short, pad it
  if (isHex(strCode)) {
    return pad(strCode as `0x${string}`, { size: 32 });
  }

  // If it's a numeric string (like "16"), return it as-is.
  // The SDK and API expect the raw numeric ID for builder attribution.
  if (/^\d+$/.test(strCode)) {
    return strCode;
  }

  return strCode;
}

/**
 * Normalizes bytes32 fields in EIP-712 message arguments.
 * Ensures they are exactly 32 bytes (64 hex chars + 0x) as required by the SDK.
 *
 * @param args - The EIP-712 signing arguments
 * @returns Normalized arguments
 */
export function normalizeBytes32Fields(args: any) {
  if (!args?.types || !args?.message) return args;

  const nextMessage = { ...args.message };

  const normalizeFromType = (typeName?: string, target?: any) => {
    if (!typeName || !target || !args.types[typeName]) return;

    for (const field of args.types[typeName]) {
      if (field?.type === 'bytes32' && typeof target[field.name] === 'string') {
        const value = target[field.name];
        if (isHex(value) && value.length < 66) {
          target[field.name] = pad(value as `0x${string}`, { size: 32 });
        }
      }
    }
  };

  // 1. Normalize root fields
  normalizeFromType(args.primaryType, nextMessage);

  return { ...args, message: nextMessage };
}

/**
 * Securely masks a sensitive key for logging purposes.
 */
export const maskKey = (value?: string | null) => {
  if (!value) return 'none';
  return value.length <= 8 ? value : `${value.slice(0, 8)}...`;
};
